import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, from, Observable, of, switchMap, throwError } from 'rxjs';
// import { environment } from '../../environments/environment';

import { PostDetailDto, PostListingDto, PostBulkDto, CreatePostRequest, UpdatePostRequest,
PostDeniedDto, CursorPageResponse,  UserDto, UpdateProfileRequest ,
CreateProfileIdRequest,
PublicProfileDto,
PersonDto, UpdateLevelRequest,
ConnectionRequestDto, CreateConnectionRequestPayload,
BlockedUserDto, TagDto, LikeResponseDto, LikeStatusDto, PostEngagementDto,
NotificationDto, UnreadCountDto,
PostMessageDto, CreatePostMessageRequest, PostMessageContextDto, MessagePageDto,
} from '../models/myapp.model';
import { environment } from 'src/environments/environment';
import { OfflineCacheService } from './offline-cache.service';


@Injectable({ providedIn: 'root' })
export class ApiService {

  private base = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private offlineCache: OfflineCacheService,
    private ngZone: NgZone
  ) {}

  // ── Posts ──────────────────────────────────────────────────────────────

  createPost(req: CreatePostRequest): Observable<PostDetailDto> {
    return this.http.post<PostDetailDto>(`${this.base}/api/posts`, req);
  }

  updatePost(postId: string, req: UpdatePostRequest, createdAt: string, token: string | null): Observable<{ type: string; data?: any }> {
    const url = `${this.base}/api/posts/${postId}?createdAt=${encodeURIComponent(createdAt)}`;
    return new Observable(observer => {
      const controller = new AbortController();
      let isDone = false;
      fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(req),
        signal: controller.signal
      }).then(response => {
        if (!response.ok) {
          response.json().then(body => {
            this.ngZone.run(() => observer.error({ status: response.status, error: body }));
          }).catch(() => {
            this.ngZone.run(() => observer.error({ status: response.status }));
          });
          return;
        }
        if (!response.body) {
          this.ngZone.run(() => observer.error({ status: 0 }));
          return;
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        const readNext = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              this.ngZone.run(() => observer.complete());
              return;
            }
            buffer += decoder.decode(value, { stream: true });
            const blocks = buffer.split('\n\n');
            buffer = blocks.pop() ?? '';
            for (const block of blocks) {
              let type = 'message';
              let dataStr = '';
              for (const line of block.split('\n')) {
                if (line.startsWith('event:')) { type = line.slice(6).trim(); }
                if (line.startsWith('data:')) { dataStr = line.slice(5).trim(); }
              }
              let data: any;
              try { data = dataStr ? JSON.parse(dataStr) : undefined; } catch { data = dataStr; }
              if (type === 'error') {
                isDone = true;
                reader.cancel().catch(() => {});
                this.ngZone.run(() => observer.error({ status: 500, error: data }));
                return;
              }
              this.ngZone.run(() => observer.next({ type, data }));
              if (type === 'D') {
                isDone = true;
                reader.cancel().catch(() => {});
                this.ngZone.run(() => observer.complete());
                return;
              }
            }
            if (!isDone) { readNext(); }
          }).catch(err => {
            if (!isDone) { this.ngZone.run(() => observer.error(err)); }
          });
        };
        readNext();
      }).catch(err => this.ngZone.run(() => observer.error(err)));
      return () => controller.abort();
    });
  }

  /**
   * Single post by UUID. Pass createdAt for partition-pruned fast lookup.
   * Without createdAt, falls back to cross-partition scan (slower, dev only).
   */
  getPost(postId: string, createdAt?: string): Observable<PostDetailDto | PostDeniedDto> {
    
    let params = new HttpParams();
    if (createdAt) params = params.set('createdAt', createdAt);
    const cacheKey = this.cacheKey('/api/posts/detail', { postId, createdAt });
    return this.cachedGet(
      cacheKey,
      this.http.get<PostDetailDto | PostDeniedDto>(
        `${this.base}/api/posts/${postId}`, { params }
      )
    );
  }

  deletePost(postId: string, createdAt: string): Observable<void> {
    const params = new HttpParams().set('createdAt', createdAt);
    return this.http.delete<void>(`${this.base}/api/posts/${postId}`, { params });
  }

  archivePost(postId: string, createdAt: string): Observable<void> {
    const params = new HttpParams().set('createdAt', createdAt);
    return this.http.post<void>(`${this.base}/api/posts/${postId}/archive`, {}, { params });
  }

  unarchivePost(postId: string, createdAt: string): Observable<void> {
    const params = new HttpParams().set('createdAt', createdAt);
    return this.http.post<void>(`${this.base}/api/posts/${postId}/unarchive`, {}, { params });
  }

  bulkArchive(posts: PostBulkDto[]): Observable<void> {
    return this.http.post<void>(`${this.base}/api/posts/bulk-archive`, posts);
  }

  bulkDelete(posts: PostBulkDto[]): Observable<void> {
    return this.http.post<void>(`${this.base}/api/posts/bulk-delete`, posts);
  }

  getMyPosts(
    showArchived: boolean,
    accessLevel?: number,
    tags?: string[],
    cursorCreatedAt?: string,
    cursorPostId?: string,
    size = 20
  ): Observable<CursorPageResponse<PostListingDto>> {
    let params = new HttpParams()
      .set('showArchived', showArchived.toString())
      .set('size', size.toString());
    if (accessLevel != null) params = params.set('accessLevel', accessLevel.toString());
    if (tags && tags.length > 0) tags.forEach(t => params = params.append('tags', t));
    if (cursorCreatedAt) params = params.set('cursorCreatedAt', cursorCreatedAt);
    if (cursorPostId) params = params.set('cursorPostId', cursorPostId);
    const cacheKey = this.cacheKey('/api/posts/my', {
      showArchived,
      accessLevel,
      tags,
      cursorCreatedAt,
      cursorPostId,
      size,
    });
    return this.cachedGet(
      cacheKey,
      this.http.get<CursorPageResponse<PostListingDto>>(
        `${this.base}/api/posts/my`, { params }
      )
    );
  }

  getWorldFeed(
    cursorPopularityIndex?: number, cursorCreatedAt?: string,
    cursorPostId?: string, size = 20, tags?: string[], windowMonths = 3
  ): Observable<CursorPageResponse<PostListingDto>> {
    let params = new HttpParams().set('size', size.toString()).set('windowMonths', windowMonths.toString());
    if (cursorPopularityIndex != null) params = params.set('cursorPopularityIndex', cursorPopularityIndex.toString());
    if (cursorCreatedAt) params = params.set('cursorCreatedAt', cursorCreatedAt);
    if (cursorPostId) params = params.set('cursorPostId', cursorPostId);
    if (tags && tags.length > 0) {
      tags.forEach(t => params = params.append('tags', t));
    }
    const cacheKey = this.cacheKey('/api/posts/world', {
      cursorPopularityIndex,
      cursorCreatedAt,
      cursorPostId,
      size,
      tags,
      windowMonths,
    });
    return this.cachedGet(
      cacheKey,
      this.http.get<CursorPageResponse<PostListingDto>>(
        `${this.base}/api/posts/world`, { params }
      )
    );
  }

  getInnerCircleFeed(
    cursorCreatedAt?: string, cursorPostId?: string, size = 20, tags?: string[], windowMonths = 3
  ): Observable<CursorPageResponse<PostListingDto>> {
    let params = new HttpParams().set('size', size.toString()).set('windowMonths', windowMonths.toString());
    if (cursorCreatedAt) { params = params.set('cursorCreatedAt', cursorCreatedAt); }
    if (cursorPostId) { params = params.set('cursorPostId', cursorPostId); }
    if (tags && tags.length > 0) { tags.forEach(t => params = params.append('tags', t));}
    const cacheKey = this.cacheKey('/api/posts/post-feed/inner-circle', {
      cursorCreatedAt,
      cursorPostId,
      size,
      tags,
      windowMonths,
    });
    return this.cachedGet(
      cacheKey,
      this.http.get<CursorPageResponse<PostListingDto>>(
        `${this.base}/api/posts/post-feed/inner-circle`, { params }
      )
    );
  }

  getConnectionFeed(
    cursorCreatedAt?: string, cursorPostId?: string, size = 20, tags?: string[], windowMonths = 3
  ): Observable<CursorPageResponse<PostListingDto>> {
    let params = new HttpParams().set('size', size.toString()).set('windowMonths', windowMonths.toString());
    if (cursorCreatedAt) params = params.set('cursorCreatedAt', cursorCreatedAt);
    if (cursorPostId) params = params.set('cursorPostId', cursorPostId);
    if (tags && tags.length > 0) tags.forEach(t => params = params.append('tags', t));
    const cacheKey = this.cacheKey('/api/posts/post-feed/connections', {
      cursorCreatedAt,
      cursorPostId,
      size,
      tags,
      windowMonths,
    });
    return this.cachedGet(
      cacheKey,
      this.http.get<CursorPageResponse<PostListingDto>>(
        `${this.base}/api/posts/post-feed/connections`, { params }
      )
    );
  }

  // ── News page (random posts for now; selection strategy TBD) ──────────

  getNewsPosts(size = 10): Observable<PostDetailDto[]> {
    const params = new HttpParams().set('size', size.toString());
    return this.http.get<PostDetailDto[]>(`${this.base}/api/posts/news`, { params });
  }

  // ── Post messages (comments + direct messages) ────────────────────────

  sendPostMessage(postId: string, createdAt: string, req: CreatePostMessageRequest): Observable<PostMessageDto> {
    const params = new HttpParams().set('createdAt', createdAt);
    return this.http.post<PostMessageDto>(`${this.base}/api/posts/${postId}/messages`, req, { params });
  }

  getComments(
    postId: string, createdAt: string,
    cursorCreatedAt?: string, cursorMessageId?: string, size = 10
  ): Observable<MessagePageDto> {
    let params = new HttpParams().set('createdAt', createdAt).set('size', size.toString());
    if (cursorCreatedAt) { params = params.set('cursorCreatedAt', cursorCreatedAt); }
    if (cursorMessageId) { params = params.set('cursorMessageId', cursorMessageId); }
    return this.http.get<MessagePageDto>(`${this.base}/api/posts/${postId}/comments`, { params });
  }

  getDirectMessages(
    postId: string, createdAt: string,
    cursorCreatedAt?: string, cursorMessageId?: string, size = 10
  ): Observable<MessagePageDto> {
    let params = new HttpParams().set('createdAt', createdAt).set('size', size.toString());
    if (cursorCreatedAt) { params = params.set('cursorCreatedAt', cursorCreatedAt); }
    if (cursorMessageId) { params = params.set('cursorMessageId', cursorMessageId); }
    return this.http.get<MessagePageDto>(`${this.base}/api/posts/${postId}/direct-messages`, { params });
  }

  getPostMessageContext(postId: string, createdAt: string): Observable<PostMessageContextDto> {
    const params = new HttpParams().set('createdAt', createdAt);
    return this.http.get<PostMessageContextDto>(`${this.base}/api/posts/${postId}/messages/context`, { params });
  }

  setCommentMarkDown(messageId: string, marked: boolean): Observable<PostMessageDto> {
    return this.http.patch<PostMessageDto>(`${this.base}/api/posts/messages/${messageId}/mark-down`, { marked });
  }

  // ── Users ─────────────────────────────────────────────────────────────

  // begin AQAQ below calls needs to be looked at later
  syncMe(): Observable<UserDto> { // this needs to be called after email login , google auth calls it internally
    return this.http.post<UserDto>(`${this.base}/api/users/me`, {});
  }

  getMe(): Observable<UserDto> {
    return this.cachedGet(
      this.cacheKey('/api/users/me'),
      this.http.get<UserDto>(`${this.base}/api/users/me`)
    );
  }

  updateMe(req: UpdateProfileRequest): Observable<UserDto> {
    return this.http.put<UserDto>(`${this.base}/api/users/me`, req);
  }

  createProfileId(req: CreateProfileIdRequest): Observable<UserDto> {
    return this.http.post<UserDto>(`${this.base}/api/users/me/profile-id`, req);
  }

  deleteMe(): Observable<void> {
    return this.http.delete<void>(`${this.base}/api/users/me`);
  }
// end AQAQ

  patchFcmToken(token: string, platform: string): Observable<void> {
    return this.http.patch<void>(`${this.base}/api/users/me/fcm-token`, { token, platform });
  }

  deleteFcmToken(platform: string): Observable<void> {
    const params = new HttpParams().set('platform', platform);
    return this.http.delete<void>(`${this.base}/api/users/me/fcm-token`, { params });
  }

  getPublicProfile(profileId: string): Observable<PublicProfileDto> {
    return this.cachedGet(
      this.cacheKey('/api/users/profile', { profileId }),
      this.http.get<PublicProfileDto>(`${this.base}/api/users/profile/${encodeURIComponent(profileId)}`)
    );
  }

  // ── Relationships ─────────────────────────────────────────────────────

  /*
  getConnections(): Observable<PersonDto[]> {
    return this.cachedGet(
      this.cacheKey('/api/relationships'),
      this.http.get<PersonDto[]>(`${this.base}/api/relationships`)
    );
  }
  */

  getInnerCircle(): Observable<PersonDto[]> {
    return this.cachedGet(
      this.cacheKey('/api/relationships/inner-circle'),
      this.http.get<PersonDto[]>(`${this.base}/api/relationships/inner-circle`)
    );
  }

  getConnectionsOnly(): Observable<PersonDto[]> {
    return this.cachedGet(
      this.cacheKey('/api/relationships/connections-only'),
      this.http.get<PersonDto[]>(`${this.base}/api/relationships/connections-only`)
    );
  }

  searchInnerCircle(emailFragment: string): Observable<PersonDto[]> {
    const params = new HttpParams().set('email', emailFragment);
    return this.http.get<PersonDto[]>(`${this.base}/api/relationships/inner-circle/search`, { params });
  }

  searchConnectionsOnly(emailFragment: string): Observable<PersonDto[]> {
    const params = new HttpParams().set('email', emailFragment);
    return this.http.get<PersonDto[]>(`${this.base}/api/relationships/connections-only/search`, { params });
  }

  getConnectionByProfileId(profileId: string): Observable<PersonDto> {
    return this.http.get<PersonDto>(
      `${this.base}/api/relationships/by-profile/${encodeURIComponent(profileId)}`
    );
  }

  updateLevel(profileId: string, req: UpdateLevelRequest): Observable<void> {
    return this.http.patch<void>(
      `${this.base}/api/relationships/by-profile/${encodeURIComponent(profileId)}/level`, req
    );
  }

  // ── Connection requests ───────────────────────────────────────────────

  sendConnectionRequest(payload: CreateConnectionRequestPayload, token: string | null): Observable<{ type: string; data?: ConnectionRequestDto }> {
    const url = `${this.base}/api/connection-requests`;
    return new Observable(observer => {
      const controller = new AbortController();
      let isDone = false;
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      }).then(response => {
        if (!response.ok) {
          response.json().then(body => {
            this.ngZone.run(() => observer.error({ status: response.status, error: body }));
          }).catch(() => {
            this.ngZone.run(() => observer.error({ status: response.status }));
          });
          return;
        }
        if (!response.body) {
          this.ngZone.run(() => observer.error({ status: 0 }));
          return;
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        const readNext = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              this.ngZone.run(() => observer.complete());
              return;
            }
            buffer += decoder.decode(value, { stream: true });
            const blocks = buffer.split('\n\n');
            buffer = blocks.pop() ?? '';
            for (const block of blocks) {
              let type = 'message';
              let dataStr = '';
              for (const line of block.split('\n')) {
                if (line.startsWith('event:')) { type = line.slice(6).trim(); }
                if (line.startsWith('data:')) { dataStr = line.slice(5).trim(); }
              }
              let data: ConnectionRequestDto | undefined;
              try { data = dataStr ? JSON.parse(dataStr) : undefined; } catch { data = undefined; }
              this.ngZone.run(() => observer.next({ type, data }));
              if (type === 'D' || type === 'error') {
                isDone = true;
                reader.cancel().catch(() => {});
                this.ngZone.run(() => observer.complete());
                return;
              }
            }
            if (!isDone) { readNext(); }
          }).catch(err => {
            if (!isDone) { this.ngZone.run(() => observer.error(err)); }
          });
        };
        readNext();
      }).catch(err => this.ngZone.run(() => observer.error(err)));
      return () => controller.abort();
    });
  }

  getConnectionRequest(id: string): Observable<ConnectionRequestDto> {
    return this.http.get<ConnectionRequestDto>(`${this.base}/api/connection-requests/${id}`);
  }

  getSentRequests(
    cursorCreatedAt?: string, cursorId?: string, size = 20
  ): Observable<CursorPageResponse<ConnectionRequestDto>> {
    let params = new HttpParams().set('size', size.toString());
    if (cursorCreatedAt) params = params.set('cursorCreatedAt', cursorCreatedAt);
    if (cursorId) params = params.set('cursorId', cursorId);
    const cacheKey = this.cacheKey('/api/connection-requests/sent', {
      cursorCreatedAt,
      cursorId,
      size,
    });
    return this.cachedGet(
      cacheKey,
      this.http.get<CursorPageResponse<ConnectionRequestDto>>(
        `${this.base}/api/connection-requests/sent`, { params }
      )
    );
  }

  getInbox(
    cursorCreatedAt?: string, cursorId?: string, size = 20
  ): Observable<CursorPageResponse<ConnectionRequestDto>> {
    let params = new HttpParams().set('size', size.toString());
    if (cursorCreatedAt) params = params.set('cursorCreatedAt', cursorCreatedAt);
    if (cursorId) params = params.set('cursorId', cursorId);
    const cacheKey = this.cacheKey('/api/connection-requests/inbox', {
      cursorCreatedAt,
      cursorId,
      size,
    });
    return this.cachedGet(
      cacheKey,
      this.http.get<CursorPageResponse<ConnectionRequestDto>>(
        `${this.base}/api/connection-requests/inbox`, { params }
      )
    );
  }

  searchSentRequests(emailFragment: string): Observable<ConnectionRequestDto[]> {
    const params = new HttpParams().set('email', emailFragment);
    return this.http.get<ConnectionRequestDto[]>(`${this.base}/api/connection-requests/sent/search`, { params });
  }

  searchInboxRequests(emailFragment: string): Observable<ConnectionRequestDto[]> {
    const params = new HttpParams().set('email', emailFragment);
    return this.http.get<ConnectionRequestDto[]>(`${this.base}/api/connection-requests/inbox/search`, { params });
  }

  acceptRequest(id: string, token: string | null): Observable<{ type: string }> {
    const url = `${this.base}/api/connection-requests/${id}/accept`;
    return new Observable(observer => {
      const controller = new AbortController();
      let isDone = false;
      fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        signal: controller.signal
      }).then(response => {
        if (!response.ok) {
          response.json().then(body => {
            this.ngZone.run(() => observer.error({ status: response.status, error: body }));
          }).catch(() => {
            this.ngZone.run(() => observer.error({ status: response.status }));
          });
          return;
        }
        if (!response.body) {
          this.ngZone.run(() => observer.error({ status: 0 }));
          return;
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        const readNext = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              this.ngZone.run(() => observer.complete());
              return;
            }
            buffer += decoder.decode(value, { stream: true });
            const blocks = buffer.split('\n\n');
            buffer = blocks.pop() ?? '';
            for (const block of blocks) {
              let type = 'message';
              for (const line of block.split('\n')) {
                if (line.startsWith('event:')) { type = line.slice(6).trim(); }
              }
              this.ngZone.run(() => observer.next({ type }));
              if (type === 'D' || type === 'error') {
                isDone = true;
                reader.cancel().catch(() => {});
                this.ngZone.run(() => observer.complete());
                return;
              }
            }
            if (!isDone) { readNext(); }
          }).catch(err => {
            if (!isDone) { this.ngZone.run(() => observer.error(err)); }
          });
        };
        readNext();
      }).catch(err => this.ngZone.run(() => observer.error(err)));
      return () => controller.abort();
    });
  }

  denyRequest(id: string): Observable<void> {
    return this.http.post<void>(`${this.base}/api/connection-requests/${id}/deny`, {});
  }

  cancelRequest(id: string): Observable<void> {
    return this.http.post<void>(`${this.base}/api/connection-requests/${id}/cancel`, {});
  }

  blockRequest(id: string): Observable<void> {
    // this block is used when the connection is not established, i.e in begnining
    return this.http.post<void>(`${this.base}/api/connection-requests/${id}/block`, {});
  }

  disconnectFromUser(profileId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/api/connection-requests/with/${encodeURIComponent(profileId)}/disconnect`, {});
  }

  blockConnection(profileId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/api/connection-requests/with/${encodeURIComponent(profileId)}/block`, {});
  }

  // ── Blocked users ─────────────────────────────────────────────────────

  getBlockedUsers(): Observable<BlockedUserDto[]> {
    return this.cachedGet(
      this.cacheKey('/api/blocked-users'),
      this.http.get<BlockedUserDto[]>(`${this.base}/api/blocked-users`)
    );
  }

  searchBlockedUsers(emailFragment: string): Observable<BlockedUserDto[]> {
    const params = new HttpParams().set('email', emailFragment);
    return this.http.get<BlockedUserDto[]>(`${this.base}/api/blocked-users/search`, { params });
  }

  unblockUser(email: string): Observable<void> {
    return this.http.post<void>(`${this.base}/api/blocked-users/unblock`, { email });
  }

  // ── Likes ─────────────────────────────────────────────────────────────

  // fetch is used because the like is a POST (write action) and EventSource
  // only supports GET. fetch also bypasses the Angular HTTP interceptor, so
  // the caller supplies the Firebase token via authService.getToken() and we
  // set it in the Authorization header here — the same header the interceptor
  // would set for HttpClient requests.
  // Emits { type: 'accepted' | 'done' | 'error' }.
  likePost(postId: string, createdAt: string, token: string | null): Observable<{ type: string }> {
    const url = `${this.base}/api/engagement/posts/${postId}/like?createdAt=${encodeURIComponent(createdAt)}`;

    return new Observable(observer => {
      const controller = new AbortController();
      let isDone = false;

      fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        signal: controller.signal
      }).then(response => {
        if (!response.ok || !response.body) {
          this.ngZone.run(() => observer.error({ status: response.status }));
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const readNext = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              this.ngZone.run(() => observer.complete());
              return;
            }
            buffer += decoder.decode(value, { stream: true });
            const blocks = buffer.split('\n\n');
            buffer = blocks.pop() ?? '';

            for (const block of blocks) {
              let type = 'message'; // ?? 
              for (const line of block.split('\n')) {
                if (line.startsWith('event:')) { type = line.slice(6).trim(); }
              }
              this.ngZone.run(() => observer.next({ type }));
              if (type === 'D' || type === 'error') { // ??
                isDone = true;
                reader.cancel().catch(() => {});
                this.ngZone.run(() => observer.complete());
                return;
              }
            }

            if (!isDone) { readNext(); }
          }).catch(err => {
            if (!isDone) {
              this.ngZone.run(() => observer.error(err));
            }
          });
        };

        readNext();
      }).catch(err => this.ngZone.run(() => observer.error(err)));

      return () => controller.abort();
    });
  }

  unlikePost(postId: string, createdAt: string, token: string | null): Observable<{ type: string }> {
    const url = `${this.base}/api/engagement/posts/${postId}/like?createdAt=${encodeURIComponent(createdAt)}`;

    return new Observable(observer => {
      const controller = new AbortController();
      let isDone = false;

      fetch(url, {
        method: 'DELETE',
        headers: {
          'Accept': 'text/event-stream',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        signal: controller.signal
      }).then(response => {
        if (!response.ok || !response.body) {
          this.ngZone.run(() => observer.error({ status: response.status }));
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const readNext = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              this.ngZone.run(() => observer.complete());
              return;
            }
            buffer += decoder.decode(value, { stream: true });
            const blocks = buffer.split('\n\n');
            buffer = blocks.pop() ?? '';

            for (const block of blocks) {
              let type = 'message';
              for (const line of block.split('\n')) {
                if (line.startsWith('event:')) { type = line.slice(6).trim(); }
              }
              this.ngZone.run(() => observer.next({ type }));
              if (type === 'D' || type === 'error') {
                isDone = true;
                reader.cancel().catch(() => {});
                this.ngZone.run(() => observer.complete());
                return;
              }
            }

            if (!isDone) { readNext(); }
          }).catch(err => {
            if (!isDone) {
              this.ngZone.run(() => observer.error(err));
            }
          });
        };

        readNext();
      }).catch(err => this.ngZone.run(() => observer.error(err)));

      return () => controller.abort();
    });
  }

  // Fetches the post and records a view in a single SSE stream.
  // Events: 'A' (post DTO as JSON), 'denied' (PostDeniedDto), 'D' (view recorded), 'error'.
  getPostWithView(postId: string, createdAt: string, token: string | null, alreadySeen: boolean): Observable<{ type: string; data?: any }> {
    const url = `${this.base}/api/posts/${postId}/view-sse?createdAt=${encodeURIComponent(createdAt)}`;

    return new Observable(observer => {
      const controller = new AbortController();
      let isDone = false;

      fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          'X-seen': alreadySeen ? 'true' : 'false',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        signal: controller.signal
      }).then(response => {
        if (!response.ok || !response.body) {
          this.ngZone.run(() => observer.error({ status: response.status }));
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const readNext = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              this.ngZone.run(() => observer.complete());
              return;
            }
            buffer += decoder.decode(value, { stream: true });
            const blocks = buffer.split('\n\n');
            buffer = blocks.pop() ?? '';

            for (const block of blocks) {
              let type = 'message';
              let dataStr = '';
              for (const line of block.split('\n')) {
                if (line.startsWith('event:')) { type = line.slice(6).trim(); }
                if (line.startsWith('data:')) { dataStr = line.slice(5).trim(); }
              }
              let data: any;
              try { data = dataStr ? JSON.parse(dataStr) : undefined; } catch { data = dataStr; }
              this.ngZone.run(() => observer.next({ type, data }));
              if (type === 'denied' || type === 'error' || type === 'D') {
                isDone = true;
                reader.cancel().catch(() => {});
                this.ngZone.run(() => observer.complete());
                return;
              }
            }

            if (!isDone) { readNext(); }
          }).catch(err => {
            if (!isDone) { this.ngZone.run(() => observer.error(err)); }
          });
        };

        readNext();
      }).catch(err => this.ngZone.run(() => observer.error(err)));

      return () => controller.abort();
    });
  }
/*
  getEngagementStatus(postId: string, createdAt: string): Observable<PostEngagementDto> { // getLikeStatus
    const params = new HttpParams().set('createdAt', createdAt);
    return this.http.get<PostEngagementDto>(`${this.base}/api/posts/${postId}/engagement-status`, { params });
  }
*/
  // ── Notifications ─────────────────────────────────────────────────────

  getMyNotifications(): Observable<NotificationDto[]> {
    return this.http.get<NotificationDto[]>(`${this.base}/api/notifications/me`);
  }

  getNotificationUnreadCount(): Observable<UnreadCountDto> {
    return this.http.get<UnreadCountDto>(`${this.base}/api/notifications/me/unread-count`);
  }

  markAllNotificationsRead(): Observable<void> {
    return this.http.post<void>(`${this.base}/api/notifications/me/mark-all-read`, {});
  }

  markNotificationReadByUrl(url: string): Observable<void> {
    return this.http.patch<void>(`${this.base}/api/notifications/me/mark-read-by-url`, { url });
  }

  // ── Tags ──────────────────────────────────────────────────────────────

  getPopularTags(): Observable<TagDto[]> {
    return this.cachedGet(
      this.cacheKey('/api/tags/popular'),
      this.http.get<TagDto[]>(`${this.base}/api/tags/popular`)
    );
  }

  private cachedGet<T>(cacheKey: string, request: Observable<T>): Observable<T> {
    return request.pipe(
      switchMap(res => from(this.offlineCache.set(cacheKey, res)).pipe(
        catchError(() => of(undefined)),
        switchMap(() => of(res))
      )),
      catchError(err => {
        if (!this.shouldUseCachedRead(err)) return throwError(() => err);

        return from(this.offlineCache.get<T>(cacheKey)).pipe(
          switchMap(cached => cached ? of(cached) : throwError(() => err))
        );
      })
    );
  }

  private shouldUseCachedRead(err: any): boolean {
    return err?.status === 0 || err?.status === 502 || err?.status === 503 || err?.status === 504;
  }

  private cacheKey(path: string, params: Record<string, unknown> = {}): string {
    const normalized = Object.keys(params)
      .filter(key => params[key] !== undefined && params[key] !== null)
      .sort()
      .map(key => `${key}=${this.normalizeCacheValue(params[key])}`)
      .join('&');
    return normalized ? `${path}?${normalized}` : path;
  }

  private normalizeCacheValue(value: unknown): string {
    return Array.isArray(value)
      ? value.map(v => String(v)).sort().join(',')
      : String(value);
  }
}
